import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { apiError } from "../lib/api";
import { ROLE_LABEL, fmtDate } from "../lib/format";
import { Card } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, ShieldCheck, Wrench, Eye } from "lucide-react";

const ROLE_ICON = { admin: ShieldCheck, gestor: Wrench, consulta: Eye };
const ROLE_DESC = {
  admin: "Ve todo en ambas propiedades, activa fases, crea usuarios, edita catálogos, exporta reportes",
  gestor: "Crea y actualiza trámites, cambia estatus, sube documentos, captura fechas y folios",
  consulta: "Solo lectura de dashboards, mapas y expedientes (propietario, Marriott, bancos)",
};

export default function Usuarios() {
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nombre: "", email: "", password: "", role: "gestor" });

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const crear = async () => {
    if (!form.nombre || !form.email || !form.password) return toast.error("Completa todos los campos");
    try {
      await api.post("/users", form);
      toast.success("Usuario creado");
      setOpen(false);
      setForm({ nombre: "", email: "", password: "", role: "gestor" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-[900px]">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-[#00A3B5] font-bold mb-1">Gestión de accesos</p>
          <h1 className="font-display text-3xl font-bold text-[#002B49]">Usuarios y roles</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="nuevo-usuario-button" className="bg-[#00A3B5] hover:bg-[#008C9C] text-white gap-2 active:scale-[0.98]">
              <UserPlus size={15} /> Nuevo usuario
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle className="font-display text-[#002B49]">Crear usuario</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input data-testid="usuario-nombre-input" placeholder="Nombre completo" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
              <Input data-testid="usuario-email-input" type="email" placeholder="Correo electrónico" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Input data-testid="usuario-password-input" type="password" placeholder="Contraseña" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="usuario-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button data-testid="crear-usuario-submit" onClick={crear} className="w-full bg-[#002B49] text-white">Crear usuario</Button>
            </div>
          </DialogContent>
        </Dialog>
      </motion.div>
      {!users ? <p className="text-slate-400 animate-pulse">Cargando usuarios...</p> : (
        <div className="space-y-3">
          {users.map((u, i) => {
            const Icon = ROLE_ICON[u.role] || Eye;
            return (
              <motion.div key={u.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}>
                <Card className="p-4 flex items-center gap-4" testId={`usuario-${u.email}`}>
                  <div className="w-10 h-10 rounded-full bg-[#EAD8C0] text-[#002B49] font-display font-bold flex items-center justify-center">
                    {u.nombre.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-800">{u.nombre}</p>
                    <p className="text-xs text-slate-500">{u.email} · creado {fmtDate(u.created_at)}</p>
                  </div>
                  <div className="text-right max-w-xs hidden md:block">
                    <p className="text-xs font-bold text-[#002B49] flex items-center justify-end gap-1"><Icon size={13} className="text-[#00A3B5]" /> {ROLE_LABEL[u.role]}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{ROLE_DESC[u.role]}</p>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
